import { getEnvByHostname } from '../utils/env';
import React, { useState } from 'react';
import { Card, Form, Input, Button, Space, message, Descriptions, Tag, Empty, Row, Col, Statistic } from 'antd';
import { SearchOutlined, ReloadOutlined, BankOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';
import { testApi } from '../services/api';

export default function BankAccountQuery() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const { cache, save: saveCache } = usePageCache('bankAccountQuery');

  React.useEffect(() => {
    if (cache) {
      if (cache.accountData) setAccountData(cache.accountData);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    }
  }, []);
  React.useEffect(() => {
    return () => { saveCache({ accountData, formValues: form.getFieldsValue() }); };
  });

  const handleQuery = async (values) => {
    setLoading(true);
    try {
      const requestBody = { recipientId: values.recipientId, requestId: values.requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` };
      const { data } = await testApi.executeTest({
        businessPartner: 'DEMO_ACCOUNT', env: getEnvByHostname(),
        testType: 'CARD_QUERY', requestBody: JSON.stringify(requestBody),
        executor: localStorage.getItem('username')
      });
      const res = data.data?.responseBody ? JSON.parse(data.data.responseBody) : data;
      if (res.code === '200' || res.code === 200) { setAccountData(res); }
      else { message.error(`${t('queryFailed')}: ${res.msg || t('queryFailed')}`); setAccountData(null); }
    } catch (error) {
      message.error(error.response?.data?.msg || t('queryFailed'));
      setAccountData(null);
    } finally { setLoading(false); }
  };

  const getStatusTag = (status) => {
    const cfg = { SUCCESS: { color: '#52c41a', icon: <CheckCircleOutlined /> }, FAILED: { color: '#ff4d4f', icon: <CloseCircleOutlined /> } };
    const c = cfg[status] || cfg.SUCCESS;
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: 14, padding: '4px 12px' }}>{status}</Tag>;
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card className="modern-card"
          title={<Space size={16}><SearchOutlined style={{ color: '#667eea', fontSize: 20 }} /><span style={{ fontSize: 20, fontWeight: 600 }}>{t('bankAccountQuery')}</span></Space>}
          extra={<Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setAccountData(null); }} type="text">{t('reset')}</Button>}
          style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleQuery} size="large">
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>{t('recipientId')}</span>} name="recipientId"
                  rules={[{ required: true, message: t('pleaseInput', t('recipientId')) }]}>
                  <Input placeholder="e.g. RP20260209SG00000086" prefix={<BankOutlined style={{ color: '#9ca3af' }} />} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>Request ID ({t('optional')})</span>} name="requestId">
                  <Input placeholder={t('autoGenerate')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} className="primary-button" style={{ height: 48 }}>{t('queryBankAccount')}</Button>
            </Form.Item>
          </Form>
        </Card>

        {accountData?.data ? (
          <div className="fade-in-up">
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {[
                { title: t('accountStatus'), value: accountData.data.status, icon: <CheckCircleOutlined style={{ color: '#667eea' }} />, fontSize: 20 },
                { title: t('channel'), value: accountData.data.channelCode, icon: <BankOutlined style={{ color: '#667eea' }} />, fontSize: 16 },
                { title: t('currency'), value: accountData.data.currency, fontSize: 24 },
                { title: t('bankCountry'), value: accountData.data.bankCountry, fontSize: 24 },
              ].map((s, i) => (
                <Col xs={24} sm={12} md={6} key={i}>
                  <Card className="modern-card" style={{ background: '#fff', borderLeft: '4px solid #667eea' }}>
                    <Statistic title={<span style={{ color: '#6b7280', fontSize: 14 }}>{s.title}</span>} value={s.value}
                      valueStyle={{ color: '#1f2937', fontSize: s.fontSize, fontWeight: 600 }} prefix={s.icon} />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="modern-card"
              title={<Space><BankOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('bankAccountDetail')}</span>{getStatusTag(accountData.data.status)}</Space>}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
                <Descriptions.Item label={t('recipientId')} span={2}><code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, fontSize: 14 }}>{accountData.data.recipientId}</code></Descriptions.Item>
                <Descriptions.Item label={t('status')}>{getStatusTag(accountData.data.status)}</Descriptions.Item>
                {accountData.data.errorCode && <Descriptions.Item label={t('errorCode')}><Tag color="error">{accountData.data.errorCode}</Tag></Descriptions.Item>}
                {accountData.data.errorMsg && <Descriptions.Item label={t('errorDetail')} span={accountData.data.errorCode ? 2 : 3}><pre style={{ margin: 0, padding: '8px 12px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, fontSize: 13, color: '#ff4d4f', fontFamily: 'Monaco, Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>{(() => { try { return JSON.stringify(JSON.parse(accountData.data.errorMsg), null, 2); } catch (e) { return accountData.data.errorMsg; } })()}</pre></Descriptions.Item>}
                <Descriptions.Item label={t('externalUserId')}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{accountData.data.externalUserId}</code></Descriptions.Item>
                <Descriptions.Item label={t('channelCode')}><Tag color="blue">{accountData.data.channelCode}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('currency')}><Tag color="orange">{accountData.data.currency}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('accountName')} span={2}><span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>{accountData.data.accountName}</span></Descriptions.Item>
                <Descriptions.Item label={t('bankCountry')}><Tag color="green">{accountData.data.bankCountry}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('accountNumber')} span={2}><code style={{ background: '#f5f5f5', padding: '6px 12px', borderRadius: 6, fontSize: 16, fontWeight: 600, letterSpacing: 1 }}>{accountData.data.accountNumber}</code></Descriptions.Item>
                <Descriptions.Item label={t('bic')}><Tag color="processing" style={{ fontSize: 14, padding: '4px 12px' }}>{accountData.data.bic}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('bankName')} span={3}><span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>{accountData.data.bankName}</span></Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
        ) : (
          <Card className="modern-card">
            <Empty description={<span style={{ color: '#6b7280', fontSize: 16 }}>{t('enterRecipientId')}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        )}
      </div>
    </div>
  );
}
